import { useMemo, useSyncExternalStore } from 'react';

import { ENVIRONMENT } from '../../../../constants';
import { useEventHandler } from '../../../../hooks/useEventHandler';
import {
  AccessToken,
  AuthSession,
  Nullable,
  TwoFactorToken,
} from '../../../../types';
import { useModuleScope } from '../../../ModuleScopeContext';
import { getAuthSessionStore } from '../../store';
import { AuthApi, AuthData } from '../../types';

export const useAuthManagement = (): {
  api: AuthApi;
  data: AuthData;
} => {
  // Resolve the storage namespace exactly as InceptPersistentStorageProvider
  // does, so the session is scoped per portal (a broker tab and a middle-office
  // tab never share/overwrite each other's tokens). getAuthSessionStore is
  // memoised per namespace, so every consumer shares one store and one cache.
  const scope = useModuleScope();
  const store = getAuthSessionStore(`incept_${ENVIRONMENT.tenant}_${scope}`);

  // localStorage is the single source of truth; the store's cached snapshot is
  // the React-facing mirror, updated by this tab's writes and by other tabs'
  // storage events alike.
  const session = useSyncExternalStore(
    store.subscribe,
    store.getSnapshot,
    store.getServerSnapshot,
  );

  // Getters read the live cache (not the render snapshot) so request headers
  // always carry the very latest token, even between renders.
  const getAccessToken = useEventHandler(
    (): Nullable<AccessToken> => store.getSnapshot().jwt ?? null,
  );

  const getTwoFactorToken = useEventHandler(
    (): Nullable<TwoFactorToken> => store.getSnapshot().twoFactorToken ?? null,
  );

  const setSession: AuthApi['setSession'] = useEventHandler(
    (next: AuthSession): void => {
      store.setSession(next);
    },
  );

  const api = useMemo(
    () => ({
      setSession,
      getAccessToken,
      getTwoFactorToken,
    }),
    [setSession, getAccessToken, getTwoFactorToken],
  );

  return {
    api,
    data: session,
  };
};
